/**
 * The wooden frame around the cloth: how wide the rails are drawn (TBL-001), and where
 * each pocket's hole is drawn in it (TBL-002).
 *
 * The rails used to be a fixed 50 mm, and the brass pocket rings reach further than
 * that — 79 mm past the cloth for a side pocket at `pocketScale` 1 — so the rings
 * poked out past the wood, and the layout, which only made room for the cloth, cut
 * them off at the edge of the canvas (CLAUDE.md §13/L36). The width is now derived
 * from the same pocket geometry the physics uses, so it cannot fall behind a change
 * to `pocketScale`.
 *
 * Rendering only: none of this affects where a ball goes. A ball drops when its centre
 * crosses the mouth line (PHY-009); the hole is drawn behind that line, and a potted
 * ball's sink animation travels into it.
 */
import type { Pocket, TableGeometry } from '@potted/sim';

/** The brass ring's radius as a multiple of the drawn hole's radius. */
export const POCKET_RING_SCALE = 1.22;

/**
 * The drawn hole's radius as a fraction of the physics throat (TBL-002). Smaller than
 * the throat, so the angled cushion ends show around it, as on a real table.
 */
const HOLE_SCALE = 0.85;

/**
 * How far behind its mouth line each hole's centre is drawn, as a fraction of the
 * throat radius (TBL-002). A corner hole stays centred on the corner, as before. A
 * side hole is set back into the rail: centred on the cushion line, half of it lay on
 * the playing surface.
 */
const HOLE_SET_BACK = { corner: 1, side: 0.6 } as const;

/**
 * How much of the rubber cushion shows from above, in metres, from the cloth edge out
 * over the rail (TBL-002). A real cushion is about 50 mm deep; a little less reads
 * better at phone size.
 */
export const CUSHION_WIDTH_M = 0.04;

/** Wood left showing outside the widest pocket ring, in metres. */
const FRAME_LIP_M = 0.014;

/** Where a pocket's hole is drawn, and how big. */
export function pocketHole(pocket: Pocket): { xM: number; yM: number; radiusM: number } {
  const back = pocket.throatRadiusM * HOLE_SET_BACK[pocket.kind];
  return {
    xM: pocket.mouthCentre.xM + pocket.outward.xM * back,
    yM: pocket.mouthCentre.yM + pocket.outward.yM * back,
    radiusM: pocket.throatRadiusM * HOLE_SCALE,
  };
}

/**
 * Rail width in metres: enough to hold every pocket ring inside the wood, plus a lip.
 * The corners of the rail are then drawn as quarter circles of this radius around the
 * corner pockets, which is what gives the table its rounded corners.
 */
export function frameWidthM(geometry: TableGeometry): number {
  const { widthM, heightM } = geometry.spec;
  let reach = 0;
  for (const pocket of geometry.pockets) {
    const hole = pocketHole(pocket);
    // How far this hole's centre sits outside the cloth: zero for a corner, which is
    // centred on the corner itself; most of a throat radius for a side hole.
    const beyond = Math.max(0, -hole.xM, hole.xM - widthM, -hole.yM, hole.yM - heightM);
    reach = Math.max(reach, beyond + hole.radiusM * POCKET_RING_SCALE);
  }
  return reach + FRAME_LIP_M;
}
